import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Crown,
  Home,
  Mail,
  Phone,
  SearchX,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";

import { Card } from "@/components/ui/card";
import type { AttendanceRosterItem } from "@/types/checkin";

export type RosterFilter =
  | "ALL"
  | "CHECKED_IN"
  | "NOT_CHECKED_IN";

type Props = {
  roster: AttendanceRosterItem[];
  loadingMemberId?: string | null;
  readOnly?: boolean;
  filter: RosterFilter;
  search: string;
  onToggleCheckIn: (
    memberId: string,
    checkedIn: boolean
  ) => void | Promise<void>;
};

type HouseholdGroup = {
  id: string;
  name: string;
  members: AttendanceRosterItem[];
};

export function AttendanceRoster({
  roster,
  loadingMemberId,
  readOnly = false,
  filter,
  search,
  onToggleCheckIn,
}: Props) {
  const groups = useMemo(
    () => buildHouseholdGroups(roster, filter, search),
    [filter, roster, search]
  );

  if (groups.length === 0) {
    return (
      <Card className="p-10 text-center">
        <SearchX
          className="mx-auto text-slate-600"
          size={32}
        />

        <p className="mt-4 font-semibold text-white">
          No members found
        </p>

        <p className="mt-2 text-sm text-slate-400">
          Try changing the search or attendance filter.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <HouseholdSection
          key={group.id}
          group={group}
          loadingMemberId={loadingMemberId}
          readOnly={readOnly}
          onToggleCheckIn={onToggleCheckIn}
        />
      ))}
    </div>
  );
}

function HouseholdSection({
  group,
  loadingMemberId,
  readOnly = false,
  onToggleCheckIn,
}: {
  group: HouseholdGroup;
  loadingMemberId?: string | null;
  readOnly?: boolean;
  onToggleCheckIn: (
    memberId: string,
    checkedIn: boolean
  ) => void | Promise<void>;
}) {
  const [expanded, setExpanded] = useState(true);

  const checkedInCount = group.members.filter(
    (item) => item.is_present
  ).length;

  const allCheckedIn =
    group.members.length > 0 &&
    checkedInCount === group.members.length;

  async function toggleEntireHousehold() {
    const nextCheckedIn = !allCheckedIn;

    for (const item of group.members) {
      if (item.is_present !== nextCheckedIn) {
        await onToggleCheckIn(
          item.member.id,
          nextCheckedIn
        );
      }
    }
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-4 border-b border-slate-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          className="flex min-w-0 items-center gap-3 text-left"
          onClick={() => setExpanded((value) => !value)}
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-400">
            {group.id === "unassigned" ? (
              <Users size={20} />
            ) : (
              <Home size={20} />
            )}
          </span>

          <span className="min-w-0">
            <span className="block truncate font-semibold text-white">
              {group.name}
            </span>

            <span className="mt-1 block text-sm text-slate-500">
              {group.members.length} member
              {group.members.length === 1 ? "" : "s"}
            </span>
          </span>
        </button>

        <div className="flex items-center justify-between gap-4 sm:justify-end">
          <button
            type="button"
            disabled={readOnly || !!loadingMemberId}
            onClick={toggleEntireHousehold}
            className={`hidden rounded-xl px-4 py-2 text-sm font-semibold transition ${
              allCheckedIn
                ? "border border-emerald-500/20 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15"
                : "bg-violet-600 text-white hover:-translate-y-0.5 hover:bg-violet-500"
            }`}
          >
            {allCheckedIn
              ? "Mark Household Not Checked In"
              : "Check In Household"}
          </button>

          <span
            className={`text-sm font-semibold ${
              checkedInCount > 0
                ? "text-emerald-400"
                : "text-slate-500"
            }`}
          >
            {checkedInCount} checked in
          </span>

          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-800 hover:text-white"
            aria-label={
              expanded
                ? `Collapse ${group.name}`
                : `Expand ${group.name}`
            }
          >
            {expanded ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronDown size={18} />
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="divide-y divide-slate-800">
          {group.members.map((item) => (
            <MemberCheckInRow
              key={item.member.id}
              item={item}
              isLoading={
                !!loadingMemberId
              }
              readOnly={readOnly}
              onToggleCheckIn={onToggleCheckIn}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function MemberCheckInRow({
  item,
  isLoading,
  readOnly,
  onToggleCheckIn,
}: {
  item: AttendanceRosterItem;
  isLoading: boolean;
  readOnly: boolean;
  onToggleCheckIn: (
    memberId: string,
    checkedIn: boolean
  ) => void | Promise<void>;
}) {
  const checkedInAt =
    item.attendance?.checked_in_at ?? null;

  return (
    <div
      className={`group relative flex flex-col gap-5 px-5 py-5 transition hover:bg-slate-800/30 lg:flex-row lg:items-center ${
        item.is_present
          ? "border-l-4 border-l-emerald-500"
          : "border-l-4 border-l-transparent"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-bold ${
            item.is_present
              ? "bg-emerald-500/15 text-emerald-300"
              : "bg-slate-800 text-slate-300"
          }`}
        >
          {getInitials(
            item.member.first_name,
            item.member.last_name
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-white">
              {item.member.first_name}{" "}
              {item.member.last_name}
            </p>

            {item.member.is_head_of_household && (
              <span className="flex items-center gap-1 rounded-full bg-violet-500/15 px-2.5 py-1 text-xs font-semibold text-violet-300">
                <Crown size={12} />
                Head of Household
              </span>
            )}

            {item.member.relationship_to_household && (
              <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-400">
                {formatRelationship(
                  item.member.relationship_to_household
                )}
              </span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
            {item.member.email && (
              <span className="flex items-center gap-1.5">
                <Mail size={13} />
                {item.member.email}
              </span>
            )}

            {item.member.phone && (
              <span className="flex items-center gap-1.5">
                <Phone size={13} />
                {item.member.phone}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:justify-end">
        <div className="min-w-[132px]">
          {item.is_present ? (
            <>
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-400">
                <CheckCircle2 size={17} />
                Checked In
              </div>

              {checkedInAt && (
                <p className="mt-1 text-xs text-slate-500">
                  {formatTime(checkedInAt)}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm font-medium text-slate-500">
              Not Checked In
            </p>
          )}
        </div>

        <button
          type="button"
          disabled={isLoading || readOnly}
          onClick={() =>
            onToggleCheckIn(
              item.member.id,
              !item.is_present
            )
          }
          className={`min-h-12 min-w-[168px] rounded-xl px-5 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
            item.is_present
              ? "border border-emerald-500/20 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10"
              : "bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-950/30"
          }`}
        >
          {isLoading
            ? "Updating..."
            : item.is_present
              ? "Undo Check-In"
              : "Check In"}
        </button>
      </div>
    </div>
  );
}

function buildHouseholdGroups(
  roster: AttendanceRosterItem[],
  filter: RosterFilter,
  search: string
): HouseholdGroup[] {
  const normalizedSearch = search.trim().toLowerCase();

  const filteredRoster = roster.filter((item) => {
    if (
      filter === "CHECKED_IN" &&
      !item.is_present
    ) {
      return false;
    }

    if (
      filter === "NOT_CHECKED_IN" &&
      item.is_present
    ) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    const searchableText = [
      item.member.first_name,
      item.member.last_name,
      item.member.email,
      item.member.phone,
      item.member.household_name,
      item.member.relationship_to_household,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedSearch);
  });

  const groupMap = new Map<
    string,
    HouseholdGroup
  >();

  filteredRoster.forEach((item) => {
    const id =
      item.member.household_id ?? "unassigned";

    const name =
      item.member.household_name ??
      "All Other Members";

    const existingGroup = groupMap.get(id);

    if (existingGroup) {
      existingGroup.members.push(item);
      return;
    }

    groupMap.set(id, {
      id,
      name,
      members: [item],
    });
  });

  return [...groupMap.values()].sort(
    (a, b) => {
      if (a.id === "unassigned") {
        return 1;
      }

      if (b.id === "unassigned") {
        return -1;
      }

      return a.name.localeCompare(b.name);
    }
  );
}

function getInitials(
  firstName: string,
  lastName: string
) {
  return `${firstName[0] ?? ""}${
    lastName[0] ?? ""
  }`.toUpperCase();
}

function formatRelationship(value: string) {
  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatTime(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}